import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API } from "../../lib/api";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../../lib/api";

const STATUSES = [
    "new",
    "in_review",
    "agent_selected",
    "agreement_sent",
    "agreement_executed",
    "matched",
    "closed",
    "archived",
];

const AGREEMENT_STATUSES = ["pending", "sent", "executed"];

// SLA prompt: subtle red dot once a lead has been in 'new' or 'in_review'
// for >= 2 business days (weekends excluded). Prompts on day 2 before
// the 3-business-day response SLA is breached on day 3.
const SLA_PROMPT_BUSINESS_DAYS = 2;
const SLA_PENDING_STATUSES = new Set(["new", "in_review"]);

function businessDaysSince(isoDate) {
    if (!isoDate) return 0;
    const start = new Date(isoDate);
    if (Number.isNaN(start.getTime())) return 0;
    const cur = new Date(start);
    cur.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    let days = 0;
    while (cur < end) {
        cur.setDate(cur.getDate() + 1);
        const d = cur.getDay();
        if (d !== 0 && d !== 6) days += 1;
    }
    return days;
}

function isSlaPrompt(lead) {
    if (!lead || !SLA_PENDING_STATUSES.has(lead.status)) return false;
    return businessDaysSince(lead.created_at) >= SLA_PROMPT_BUSINESS_DAYS;
}

const EMPTY_CANDIDATE = {
    name: "",
    brokerage: "",
    email: "",
    phone: "",
    trade_area: "",
    notes: "",
};

export default function AdminLeads() {
    const [leads, setLeads] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const deepLinkId = searchParams.get("id");

    const load = useCallback(async () => {
        try {
            const r = await api.get("/leads");
            const data = r.data || [];
            setLeads(data);
            // Refresh the open modal from server data
            setSelected((cur) => (cur ? data.find((l) => l.id === cur.id) || null : cur));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!deepLinkId || leads.length === 0) return;
        const match = leads.find((l) => l.id === deepLinkId);
        if (match) {
            setSelected(match);
        } else {
            toast.error("Lead not found — it may have been deleted.");
            setSearchParams({}, { replace: true });
        }
    }, [deepLinkId, leads, setSearchParams]);

    const closeDetail = () => {
        setSelected(null);
        if (deepLinkId) setSearchParams({}, { replace: true });
    };

    const patchLead = async (id, body) => {
        try {
            const { data } = await api.patch(`/leads/${id}`, body);
            toast.success("Lead updated");
            await load();
            setSelected(data);
            return data;
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update");
        }
    };

    const removeLead = async (id) => {
        if (!window.confirm("Delete this lead? This also removes the uploaded agreement PDF.")) return;
        try {
            await api.delete(`/leads/${id}`);
            toast.success("Lead deleted");
            closeDetail();
            await load();
        } catch {
            toast.error("Could not delete");
        }
    };

    const toggleOne = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const allVisibleIds = leads.map((l) => l.id);
    const allSelected = leads.length > 0 && allVisibleIds.every((id) => selectedIds.has(id));
    const someSelected = selectedIds.size > 0 && !allSelected;

    const toggleAll = () => {
        setSelectedIds(() => (allSelected ? new Set() : new Set(allVisibleIds)));
    };

    const clearSelection = () => setSelectedIds(new Set());

    const bulkDelete = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (!window.confirm(
            `Delete ${ids.length} lead${ids.length === 1 ? "" : "s"}? This also removes any uploaded agreement PDFs and audit history. This cannot be undone.`
        )) return;
        setBulkDeleting(true);
        const results = await Promise.allSettled(ids.map((id) => api.delete(`/leads/${id}`)));
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const failed = results.length - ok;
        if (ok > 0) toast.success(`Deleted ${ok} lead${ok === 1 ? "" : "s"}`);
        if (failed > 0) toast.error(`${failed} could not be deleted`);
        setSelectedIds(new Set());
        if (selected && ids.includes(selected.id)) closeDetail();
        await load();
        setBulkDeleting(false);
    };

    return (
        <div className="p-10" data-testid="admin-leads">
            <h1 className="font-serif text-3xl text-brand-ink mb-2">Leads</h1>
            <p className="text-sm text-brand-muted mb-8">All referral submissions, newest first.</p>

            {selectedIds.size > 0 && (
                <div
                    className="flex items-center justify-between mb-4 px-4 py-3 bg-brand-ink text-brand-paper"
                    data-testid="bulk-toolbar"
                >
                    <div className="text-sm" data-testid="bulk-selected-count">
                        {selectedIds.size} selected
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={clearSelection}
                            className="text-xs uppercase tracking-wider px-3 py-1.5 text-brand-paper/80 hover:text-brand-paper"
                            data-testid="bulk-clear-button"
                        >
                            Clear
                        </button>
                        <button
                            type="button"
                            onClick={bulkDelete}
                            disabled={bulkDeleting}
                            data-testid="bulk-delete-button"
                            className="text-xs uppercase tracking-wider px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                        >
                            {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size}`}
                        </button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-brand-muted text-sm">Loading…</div>
            ) : leads.length === 0 ? (
                <div className="text-brand-muted text-sm" data-testid="leads-empty">No leads yet.</div>
            ) : (
                <div className="border border-brand-line">
                    <table className="w-full text-sm">
                        <thead className="bg-brand-sand">
                            <tr className="text-left text-xs uppercase tracking-wider text-brand-muted">
                                <th className="px-4 py-3 w-10">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                        onChange={toggleAll}
                                        data-testid="bulk-select-all"
                                        aria-label="Select all leads"
                                        className="accent-brand-ink cursor-pointer"
                                    />
                                </th>
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Location</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Agreement</th>
                                <th className="px-4 py-3">Submitted</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads.map((lead) => (
                                <tr
                                    key={lead.id}
                                    className={`border-t border-brand-line hover:bg-brand-sand/50 cursor-pointer ${selectedIds.has(lead.id) ? "bg-brand-sand/40" : ""}`}
                                    onClick={() => setSelected(lead)}
                                    data-testid={`lead-row-${lead.id}`}
                                >
                                    <td
                                        className="px-4 py-3"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(lead.id)}
                                            onChange={() => toggleOne(lead.id)}
                                            data-testid={`lead-checkbox-${lead.id}`}
                                            aria-label={`Select ${lead.first_name} ${lead.last_name}`}
                                            className="accent-brand-ink cursor-pointer"
                                        />
                                    </td>                                    <td className="px-4 py-3 text-brand-ink">
                                        <span className="inline-flex items-center gap-2">
                                            {isSlaPrompt(lead) && (
                                                <span
                                                    title={`Awaiting response · ${businessDaysSince(lead.created_at)} business days since submission (3-day SLA)`}
                                                    data-testid={`sla-prompt-dot-${lead.id}`}
                                                    className="inline-block w-2 h-2 rounded-full bg-red-500"
                                                    aria-label="SLA prompt"
                                                />
                                            )}
                                            <span>{lead.first_name} {lead.last_name}</span>
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">{lead.email}</td>
                                    <td className="px-4 py-3">{lead.search_location || lead.city || "—"}</td>
                                    <td className="px-4 py-3">
                                        <span className="inline-block px-2 py-0.5 text-xs bg-brand-paper border border-brand-line">
                                            {lead.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="inline-block px-2 py-0.5 text-xs bg-brand-paper border border-brand-line">
                                            {lead.referral_agreement_status || "pending"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-brand-muted">
                                        {new Date(lead.created_at).toLocaleDateString("en-CA")}
                                    </td>
                                    <td className="px-4 py-3 text-brand-ink underline">View</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selected && (
                <LeadDetailModal
                    lead={selected}
                    onClose={closeDetail}
                    onPatchLead={patchLead}
                    onDelete={removeLead}
                    reload={load}
                    setSelected={setSelected}
                />
            )}
        </div>
    );
}

function LeadDetailModal({ lead, onClose, onPatchLead, onDelete, reload, setSelected }) {
    return (
        <div
            className="fixed inset-0 bg-brand-ink/50 z-50 flex items-start justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="lead-detail-modal"
        >
            <div
                className="bg-brand-paper border border-brand-line w-full max-w-3xl my-8"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-brand-line flex items-center justify-between sticky top-0 bg-brand-paper z-10">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold">
                            Lead detail
                        </div>
                        <h2 className="font-serif text-2xl text-brand-ink mt-1 flex items-center gap-3">
                            {isSlaPrompt(lead) && (
                                <span
                                    title={`Awaiting response · ${businessDaysSince(lead.created_at)} business days since submission (3-day SLA)`}
                                    data-testid="sla-prompt-modal-dot"
                                    className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 shrink-0"
                                    aria-label="SLA prompt"
                                />
                            )}
                            <span>{lead.first_name} {lead.last_name}</span>
                        </h2>
                        {isSlaPrompt(lead) && (
                            <div
                                className="mt-2 text-xs text-red-700"
                                data-testid="sla-prompt-banner"
                            >
                                Awaiting first response — {businessDaysSince(lead.created_at)} business days since submission (3-day SLA).
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} className="text-brand-muted hover:text-brand-ink text-xl" data-testid="close-lead-modal">
                        ✕
                    </button>
                </div>

                <div className="p-6 space-y-6 text-sm">
                    <section>
                        <Row label="Email" value={lead.email} />
                        <Row label="Phone" value={lead.phone} />
                        <Row label="City / Province" value={`${lead.city || "—"}, ${lead.province || "—"}`} />
                        <Row label="Postal" value={lead.postal_code || "—"} />
                        <Row label="Working with agent?" value={lead.working_with_agent || "—"} />
                        <Row label="Property type" value={lead.property_type || "—"} />
                        <Row label="Search location" value={lead.search_location || "—"} />
                        <Row label="Search criteria" value={lead.search_criteria || "—"} />
                        <Row label="Agent skills" value={lead.agent_skills || "—"} />
                        <Row label="Matching notes" value={lead.matching_notes || "—"} />
                        <Row label="Source" value={lead.source || "—"} />
                    </section>

                    <StatusSection lead={lead} onPatchLead={onPatchLead} />

                    <CandidateAgentsSection lead={lead} reload={reload} setSelected={setSelected} />

                    <ReferralBriefSection lead={lead} />

                    <AgreementSection lead={lead} reload={reload} setSelected={setSelected} onPatchLead={onPatchLead} />

                    <AdminNotesSection lead={lead} onPatchLead={onPatchLead} />

                    <ConsentSection lead={lead} />

                    <ActivitySection lead={lead} />

                    <div className="pt-4 border-t border-brand-line">
                        <button
                            onClick={() => onDelete(lead.id)}
                            data-testid="lead-delete-button"
                            className="text-sm text-red-700 hover:text-red-900"
                        >
                            Delete lead
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatusSection({ lead, onPatchLead }) {
    return (
        <section className="pt-4 border-t border-brand-line space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold">Workflow</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                    <span className="text-xs uppercase tracking-wider text-brand-muted">Lead status</span>
                    <select
                        value={lead.status}
                        onChange={(e) => onPatchLead(lead.id, { status: e.target.value })}
                        data-testid="lead-status-select"
                        className="mt-2 w-full bg-transparent border border-brand-line p-2 text-brand-ink"
                    >
                        {STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </label>
                <label className="block">
                    <span className="text-xs uppercase tracking-wider text-brand-muted">Referral agreement</span>
                    <select
                        value={lead.referral_agreement_status || "pending"}
                        onChange={(e) => onPatchLead(lead.id, { referral_agreement_status: e.target.value })}
                        data-testid="agreement-status-select"
                        className="mt-2 w-full bg-transparent border border-brand-line p-2 text-brand-ink"
                    >
                        {AGREEMENT_STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </label>
            </div>
            {lead.referral_agreement_status === "executed" && lead.agreement_executed_at && (
                <p className="text-xs text-brand-muted">
                    Agreement marked executed at {new Date(lead.agreement_executed_at).toLocaleString("en-CA")} — the prospect &amp; selected agent were emailed automatically (you BCC'd).
                </p>
            )}
        </section>
    );
}

function CandidateAgentsSection({ lead, reload, setSelected }) {
    const [adding, setAdding] = useState(false);
    const candidates = lead.candidate_agents || [];

    const canAddMore = candidates.length < 3;

    const addCandidate = async (input) => {
        try {
            const { data } = await api.post(`/leads/${lead.id}/candidates`, input);
            toast.success("Candidate added");
            setSelected(data);
            await reload();
            setAdding(false);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not add candidate");
        }
    };

    const updateCandidate = async (candidateId, input) => {
        try {
            const { data } = await api.patch(`/leads/${lead.id}/candidates/${candidateId}`, input);
            toast.success("Candidate updated");
            setSelected(data);
            await reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not update candidate");
        }
    };

    const removeCandidate = async (candidateId) => {
        if (!window.confirm("Remove this candidate agent?")) return;
        try {
            const { data } = await api.delete(`/leads/${lead.id}/candidates/${candidateId}`);
            toast.success("Candidate removed");
            setSelected(data);
            await reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not remove candidate");
        }
    };

    const selectCandidate = async (candidateId) => {
        try {
            const { data } = await api.post(`/leads/${lead.id}/select-candidate/${candidateId}`);
            toast.success("Candidate selected");
            setSelected(data);
            await reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not select candidate");
        }
    };

    return (
        <section className="pt-4 border-t border-brand-line" data-testid="candidate-agents-section">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold">
                    Candidate agents ({candidates.length}/3)
                </div>
                {canAddMore && !adding && (
                    <button
                        type="button"
                        onClick={() => setAdding(true)}
                        data-testid="add-candidate-button"
                        className="text-xs uppercase tracking-wider px-3 py-1.5 bg-brand-ink text-brand-paper hover:bg-brand-ink/90"
                    >
                        + Add candidate
                    </button>
                )}
            </div>

            {candidates.length === 0 && !adding && (
                <p className="text-sm text-brand-muted">No candidates added yet. Add up to 3 vetted agents for this client.</p>
            )}

            <div className="space-y-3">
                {candidates.map((c) => (
                    <CandidateCard
                        key={c.id}
                        candidate={c}
                        isSelected={lead.selected_agent_id === c.id}
                        onSelect={() => selectCandidate(c.id)}
                        onUpdate={(input) => updateCandidate(c.id, input)}
                        onRemove={() => removeCandidate(c.id)}
                    />
                ))}
            </div>

            {adding && (
                <div className="mt-3">
                    <CandidateForm
                        initial={EMPTY_CANDIDATE}
                        onSave={addCandidate}
                        onCancel={() => setAdding(false)}
                        submitLabel="Add candidate"
                    />
                </div>
            )}
        </section>
    );
}

function CandidateCard({ candidate, isSelected, onSelect, onUpdate, onRemove }) {
    const [editing, setEditing] = useState(false);

    if (editing) {
        return (
            <div className="border border-brand-line p-4 bg-brand-paper">
                <CandidateForm
                    initial={candidate}
                    onSave={async (input) => {
                        await onUpdate(input);
                        setEditing(false);
                    }}
                    onCancel={() => setEditing(false)}
                    submitLabel="Save changes"
                />
            </div>
        );
    }

    return (
        <div
            className={`border p-4 ${isSelected ? "border-brand-gold bg-brand-sand/40" : "border-brand-line"}`}
            data-testid={`candidate-card-${candidate.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-serif text-lg text-brand-ink">{candidate.name}</span>
                        {isSelected && (
                            <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 bg-brand-gold text-brand-paper">
                                Selected
                            </span>
                        )}
                    </div>
                    {candidate.brokerage && (
                        <div className="text-xs text-brand-muted">{candidate.brokerage}</div>
                    )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {!isSelected && (
                        <button
                            type="button"
                            onClick={onSelect}
                            data-testid={`select-candidate-${candidate.id}`}
                            className="text-xs uppercase tracking-wider px-3 py-1.5 border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-brand-paper"
                        >
                            Select
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setEditing(true)}
                        className="text-xs text-brand-muted hover:text-brand-ink underline"
                    >
                        Edit
                    </button>
                    <button
                        type="button"
                        onClick={onRemove}
                        className="text-xs text-red-700 hover:text-red-900 underline"
                    >
                        Remove
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-xs text-brand-ink/80">
                {candidate.email && <div><span className="text-brand-muted uppercase tracking-wider mr-2">Email</span>{candidate.email}</div>}
                {candidate.phone && <div><span className="text-brand-muted uppercase tracking-wider mr-2">Phone</span>{candidate.phone}</div>}
                {candidate.trade_area && <div><span className="text-brand-muted uppercase tracking-wider mr-2">Area</span>{candidate.trade_area}</div>}
            </div>
            {candidate.notes && (
                <p className="mt-2 text-xs text-brand-muted whitespace-pre-wrap">{candidate.notes}</p>
            )}
        </div>
    );
}

function CandidateForm({ initial, onSave, onCancel, submitLabel }) {
    const [form, setForm] = useState(() => ({
        name: initial.name || "",
        brokerage: initial.brokerage || "",
        email: initial.email || "",
        phone: initial.phone || "",
        trade_area: initial.trade_area || "",
        notes: initial.notes || "",
    }));
    const [saving, setSaving] = useState(false);

    const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Name is required.");
            return;
        }
        setSaving(true);
        await onSave({
            name: form.name.trim(),
            brokerage: form.brokerage.trim() || null,
            email: form.email.trim() || null,
            phone: form.phone.trim() || null,
            trade_area: form.trade_area.trim() || null,
            notes: form.notes.trim() || null,
        });
        setSaving(false);
    };

    return (
        <form onSubmit={submit} className="space-y-3 border border-brand-line p-4 bg-brand-paper" data-testid="candidate-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Name *" value={form.name} onChange={change("name")} testid="candidate-name" />
                <Field label="Brokerage" value={form.brokerage} onChange={change("brokerage")} testid="candidate-brokerage" />
                <Field label="Email" value={form.email} onChange={change("email")} testid="candidate-email" />
                <Field label="Phone" value={form.phone} onChange={change("phone")} testid="candidate-phone" />
                <Field label="Trade area" value={form.trade_area} onChange={change("trade_area")} testid="candidate-area" />
            </div>
            <label className="block">
                <span className="text-xs uppercase tracking-wider text-brand-muted">Notes</span>
                <textarea
                    value={form.notes}
                    onChange={change("notes")}
                    rows={2}
                    className="mt-2 w-full bg-transparent border border-brand-line p-2 text-brand-ink text-sm"
                />
            </label>
            <div className="flex items-center gap-3">
                <button
                    type="submit"
                    disabled={saving}
                    data-testid="save-candidate-button"
                    className="text-xs uppercase tracking-wider px-4 py-2 bg-brand-ink text-brand-paper hover:bg-brand-ink/90 disabled:opacity-50"
                >
                    {saving ? "Saving…" : submitLabel}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-xs uppercase tracking-wider text-brand-muted hover:text-brand-ink"
                >
                    Cancel
                </button>
            </div>
        </form>
    );
}

function Field({ label, value, onChange, testid }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-wider text-brand-muted">{label}</span>
            <input
                type="text"
                value={value}
                onChange={onChange}
                data-testid={testid}
                className="mt-2 w-full bg-transparent border border-brand-line p-2 text-brand-ink text-sm"
            />
        </label>
    );
}

function ReferralBriefSection({ lead }) {
    const [brief, setBrief] = useState("");
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const textareaRef = useRef(null);

    const fetchBrief = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/leads/${lead.id}/brief`);
            setBrief(data.brief || "");
            setOpen(true);
        } catch {
            toast.error("Could not load brief");
        } finally {
            setLoading(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(brief);
            toast.success("Brief copied to clipboard");
        } catch {
            // Fallback: select textarea
            textareaRef.current?.select();
            document.execCommand("copy");
            toast.success("Brief copied to clipboard");
        }
    };

    return (
        <section className="pt-4 border-t border-brand-line" data-testid="referral-brief-section">
            <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold">
                    Referral agreement brief
                </div>
                {!open ? (
                    <button
                        type="button"
                        onClick={fetchBrief}
                        disabled={loading}
                        data-testid="show-brief-button"
                        className="text-xs uppercase tracking-wider px-3 py-1.5 bg-brand-ink text-brand-paper hover:bg-brand-ink/90 disabled:opacity-50"
                    >
                        {loading ? "Loading…" : "Show brief"}
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={copy}
                            data-testid="copy-brief-button"
                            className="text-xs uppercase tracking-wider px-3 py-1.5 bg-brand-ink text-brand-paper hover:bg-brand-ink/90"
                        >
                            Copy
                        </button>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="text-xs uppercase tracking-wider px-3 py-1.5 border border-brand-line text-brand-muted hover:text-brand-ink"
                        >
                            Hide
                        </button>
                    </div>
                )}
            </div>
            <p className="text-xs text-brand-muted mb-2">
                Paste this into OREA Form 641 (Referral Agreement) when preparing WEBForms. Includes the 25% referral fee preamble plus the client &amp; selected agent details.
            </p>
            {open && (
                <textarea
                    ref={textareaRef}
                    value={brief}
                    readOnly
                    rows={14}
                    data-testid="referral-brief-textarea"
                    className="w-full bg-brand-paper border border-brand-line p-3 text-xs font-mono text-brand-ink/90"
                />
            )}
        </section>
    );
}

function AgreementSection({ lead, reload, setSelected, onPatchLead }) {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const token = localStorage.getItem("rr_token");
            const res = await fetch(`${API}/leads/${lead.id}/agreement/upload`, {
                method: "POST",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: fd,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(formatApiErrorDetail(err?.detail) || "Upload failed");
            }
            const data = await res.json();
            toast.success("Agreement PDF uploaded");
            setSelected(data);
            await reload();
        } catch (err) {
            toast.error(err.message || "Could not upload");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const onDownload = async () => {
        try {
            const token = localStorage.getItem("rr_token");
            const res = await fetch(`${API}/leads/${lead.id}/agreement/download`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = lead.agreement_pdf_filename || "agreement.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Could not download");
        }
    };

    const onRemove = async () => {
        if (!window.confirm("Remove the uploaded agreement PDF?")) return;
        try {
            const { data } = await api.delete(`/leads/${lead.id}/agreement`);
            toast.success("Agreement removed");
            setSelected(data);
            await reload();
        } catch {
            toast.error("Could not remove");
        }
    };

    const onMarkExecuted = async () => {
        if (!window.confirm("Mark agreement as executed and send the introduction email to the prospect & selected agent (you BCC'd)?")) return;
        await onPatchLead(lead.id, { referral_agreement_status: "executed" });
    };

    const onDownloadCaseFile = async () => {
        try {
            const token = localStorage.getItem("rr_token");
            const res = await fetch(`${API}/leads/${lead.id}/case-file`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const safeName = `${lead.first_name || ""}_${lead.last_name || ""}`.replace(/[^A-Za-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "") || "lead";
            a.download = `case-file-${safeName}-${lead.id.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Could not download case file");
        }
    };

    const hasPdf = !!lead.agreement_pdf_id;

    return (
        <section className="pt-4 border-t border-brand-line space-y-3" data-testid="agreement-section">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold">
                Executed agreement PDF
            </div>
            {hasPdf ? (
                <div className="border border-brand-line p-3 bg-brand-paper">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-sm text-brand-ink">{lead.agreement_pdf_filename || "agreement.pdf"}</div>
                            {lead.agreement_pdf_uploaded_at && (
                                <div className="text-xs text-brand-muted">
                                    Uploaded {new Date(lead.agreement_pdf_uploaded_at).toLocaleString("en-CA")}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <button
                                type="button"
                                onClick={onDownload}
                                data-testid="download-pdf-button"
                                className="text-xs uppercase tracking-wider px-3 py-1.5 border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-brand-paper"
                            >
                                Download
                            </button>
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="text-xs uppercase tracking-wider px-3 py-1.5 border border-brand-line text-brand-muted hover:text-brand-ink"
                            >
                                Replace
                            </button>
                            <button
                                type="button"
                                onClick={onRemove}
                                className="text-xs uppercase tracking-wider px-3 py-1.5 text-red-700 hover:text-red-900"
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <p className="text-sm text-brand-muted">No PDF uploaded yet. Upload the fully-signed Referral Agreement when you have it.</p>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={onUpload}
                className="hidden"
                data-testid="agreement-pdf-input"
            />

            <div className="flex flex-wrap gap-2">
                {!hasPdf && (
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        data-testid="upload-pdf-button"
                        className="text-xs uppercase tracking-wider px-3 py-1.5 bg-brand-ink text-brand-paper hover:bg-brand-ink/90 disabled:opacity-50"
                    >
                        {uploading ? "Uploading…" : "Upload PDF"}
                    </button>
                )}
                {hasPdf && lead.referral_agreement_status !== "executed" && (
                    <button
                        type="button"
                        onClick={onMarkExecuted}
                        data-testid="mark-executed-button"
                        className="text-xs uppercase tracking-wider px-4 py-2 bg-brand-gold text-brand-paper hover:opacity-90"
                    >
                        Mark executed &amp; send intro email
                    </button>
                )}
                <button
                    type="button"
                    onClick={onDownloadCaseFile}
                    data-testid="download-case-file-button"
                    className="text-xs uppercase tracking-wider px-4 py-2 border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-brand-paper"
                >
                    Download case file (PDF)
                </button>
            </div>
            <p className="text-xs text-brand-muted">
                The case file bundles the cover sheet, OREA Form 641 brief, consent record, and (if uploaded) the executed agreement into a single PDF for your records.
            </p>
            {!lead.selected_agent_id && (
                <p className="text-xs text-amber-700">
                    Heads up: no candidate is marked as <em>Selected</em>. The intro email won't include receiving-agent details until you select one above.
                </p>
            )}
        </section>
    );
}

function AdminNotesSection({ lead, onPatchLead }) {
    const [value, setValue] = useState(lead.admin_notes || "");
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setValue(lead.admin_notes || "");
        setDirty(false);
    }, [lead.admin_notes, lead.id]);

    const save = async () => {
        await onPatchLead(lead.id, { admin_notes: value });
        setDirty(false);
    };

    return (
        <section className="pt-4 border-t border-brand-line" data-testid="admin-notes-section">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold mb-2">
                Internal admin notes
            </div>
            <textarea
                value={value}
                onChange={(e) => { setValue(e.target.value); setDirty(true); }}
                rows={3}
                data-testid="admin-notes-textarea"
                placeholder="Private notes about this lead (visible only to admins)."
                className="w-full bg-transparent border border-brand-line p-2 text-brand-ink text-sm"
            />
            {dirty && (
                <button
                    type="button"
                    onClick={save}
                    data-testid="save-notes-button"
                    className="mt-2 text-xs uppercase tracking-wider px-3 py-1.5 bg-brand-ink text-brand-paper hover:bg-brand-ink/90"
                >
                    Save notes
                </button>
            )}
        </section>
    );
}

function ConsentSection({ lead }) {
    return (
        <section className="pt-4 border-t border-brand-line">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold mb-3">
                Consent record (PIPEDA)
            </div>
            <Row label="Matching consent" value={lead.consent_matching ? "Yes" : "No"} />
            <Row label="Marketing consent" value={lead.consent_marketing ? "Yes (CASL)" : "No"} />
            <Row label="Consented at" value={lead.consent_timestamp || "—"} />
            <Row label="IP address" value={lead.consent_ip || "—"} />
            <Row label="User agent" value={lead.consent_user_agent || "—"} />
            <Row label="Policy version" value={lead.privacy_policy_version || "—"} />
        </section>
    );
}

const EVENT_LABELS = {
    lead_created: "Lead created",
    status_changed: "Status changed",
    agreement_status_changed: "Agreement status",
    notes_updated: "Notes updated",
    candidate_added: "Candidate added",
    candidate_updated: "Candidate updated",
    candidate_removed: "Candidate removed",
    candidate_selected: "Candidate selected",
    agreement_pdf_uploaded: "PDF uploaded",
    agreement_pdf_removed: "PDF removed",
    agreement_executed_email_queued: "Intro email sent",
};

const EVENT_DOT_COLOR = {
    lead_created: "bg-brand-gold",
    candidate_selected: "bg-brand-gold",
    agreement_pdf_uploaded: "bg-brand-gold",
    agreement_executed_email_queued: "bg-brand-gold",
    candidate_removed: "bg-red-400",
    agreement_pdf_removed: "bg-red-400",
};

function ActivitySection({ lead }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/leads/${lead.id}/events`);
            setEvents(data || []);
            setOpen(true);
        } catch {
            toast.error("Could not load activity");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="pt-4 border-t border-brand-line" data-testid="activity-section">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-gold font-semibold">
                    Activity log
                </div>
                {!open ? (
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        data-testid="show-activity-button"
                        className="text-xs uppercase tracking-wider px-3 py-1.5 bg-brand-ink text-brand-paper hover:bg-brand-ink/90 disabled:opacity-50"
                    >
                        {loading ? "Loading…" : "Show activity"}
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={load}
                            data-testid="refresh-activity-button"
                            className="text-xs uppercase tracking-wider px-3 py-1.5 border border-brand-line text-brand-muted hover:text-brand-ink"
                        >
                            Refresh
                        </button>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="text-xs uppercase tracking-wider px-3 py-1.5 border border-brand-line text-brand-muted hover:text-brand-ink"
                        >
                            Hide
                        </button>
                    </div>
                )}
            </div>
            {open && (
                events.length === 0 ? (
                    <p className="text-sm text-brand-muted">No activity recorded yet.</p>
                ) : (
                    <ol className="space-y-3" data-testid="activity-list">
                        {events.map((evt) => (
                            <li key={evt.id} className="flex gap-3" data-testid={`activity-item-${evt.id}`}>
                                <div className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${EVENT_DOT_COLOR[evt.type] || "bg-brand-line"}`} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                        <div className="text-sm text-brand-ink">
                                            <span className="text-xs uppercase tracking-wider text-brand-muted mr-2">
                                                {EVENT_LABELS[evt.type] || evt.type}
                                            </span>
                                            {evt.summary}
                                        </div>
                                        <div className="text-xs text-brand-muted shrink-0">
                                            {new Date(evt.created_at).toLocaleString("en-CA")}
                                        </div>
                                    </div>
                                    <div className="text-xs text-brand-muted">by {evt.actor}</div>
                                </div>
                            </li>
                        ))}
                    </ol>
                )
            )}
        </section>
    );
}

function Row({ label, value }) {
    return (
        <div className="grid grid-cols-3 gap-4 py-1">
            <div className="text-xs uppercase tracking-wider text-brand-muted">{label}</div>
            <div className="col-span-2 text-brand-ink whitespace-pre-wrap text-sm">{value}</div>
        </div>
    );
}
